import { useSelector } from 'react-redux';
import { Scrollbars } from 'react-custom-scrollbars-2';
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Grid,
    GridItem,
    Box,
} from '@chakra-ui/react';
import { LoadingIndicator, Text, AccordionIcon } from '@/components/v4';
import { renderTrack, renderView } from '@/components/sidebar/Scrollbar';
import {
    renderThumb,
    renderCrudCheckboxes,
} from './PermissionsListRenderHelpers';

import './style.css';
import config from '../../../config.json';
import { mappedPermissions } from './utils';
import { CRUD_MODES, SCOPES } from './constants';

const PermissionsList = ({ disabled, data, submitData, setSubmitData }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const borderColorStyle = darkMode ? 'dark.tcap-borderGray' : 'gray.200';
    const permissions = data?.permissions;

    if (!permissions) {
        return <LoadingIndicator />;
    }

    const formattedPermissions = mappedPermissions(permissions);
    // Only show scopes at/below this set's own tier -- never above.
    const visibleScopes = SCOPES.filter(
        (scopeNumber) => scopeNumber <= data.requiredScope,
    );
    // One visible scope means nothing left to expand -- render as a flat row.
    const isSingleScope = visibleScopes.length === 1;

    const handleOnChange = ({ e, permissionName, scopeNumber, mode }) => {
        const permission = formattedPermissions[permissionName]?.[scopeNumber];

        if (!permission?.id) {
            return;
        }

        const newSubmitData = { ...submitData };
        if (!newSubmitData[permission.id]) {
            newSubmitData[permission.id] = {
                ...permission,
                [mode]: e.target.checked,
            };
        } else {
            newSubmitData[permission.id][mode] = e.target.checked;
        }
        setSubmitData(newSubmitData);
    };

    return (
        <div id="permissions-table-component">
            <div className="table-content">
                <Box
                    as={Scrollbars}
                    borderWidth="1px"
                    borderColor={borderColorStyle}
                    borderRadius="md"
                    autoHeight
                    autoHeightMin={0}
                    autoHeightMax="60vh"
                    autoHide
                    renderTrackVertical={renderTrack}
                    renderThumbVertical={renderThumb(darkMode)}
                    renderView={renderView}>
                    <Grid
                        templateColumns="repeat(12, 1fr)"
                        position="sticky"
                        top={0}
                        zIndex={1}
                        bg={darkMode ? 'gray.800' : 'gray.100'}
                        borderBottomWidth="1px"
                        borderColor={borderColorStyle}
                        py={2}
                        px={4}>
                        <GridItem colSpan={4}>
                            <Text fontWeight="semibold">Permission</Text>
                        </GridItem>
                        {CRUD_MODES.map((mode) => (
                            <GridItem colSpan={2} key={mode}>
                                <Text fontWeight="semibold">{mode}</Text>
                            </GridItem>
                        ))}
                    </Grid>
                    {isSingleScope ? (
                        Object.entries(formattedPermissions)
                            .sort((left, right) =>
                                left[0].localeCompare(right[0]),
                            )
                            .map(([permissionName, value]) => (
                                <Box
                                    key={permissionName}
                                    className="table-data"
                                    px={4}
                                    py={2}
                                    borderBottomWidth="1px"
                                    borderColor={borderColorStyle}>
                                    <Grid templateColumns="repeat(12, 1fr)">
                                        <GridItem colSpan={4}>
                                            <Text>{permissionName}</Text>
                                        </GridItem>
                                        {renderCrudCheckboxes({
                                            permissionName,
                                            value,
                                            scopeNumber: visibleScopes[0],
                                            disabled,
                                            submitData,
                                            onChange: handleOnChange,
                                            mb: 0,
                                        })}
                                    </Grid>
                                </Box>
                            ))
                    ) : (
                        <Accordion allowMultiple allowToggle>
                            {Object.entries(formattedPermissions)
                                .sort((left, right) =>
                                    left[0].localeCompare(right[0]),
                                )
                                .map(([permissionName, value]) => (
                                    <AccordionItem key={permissionName}>
                                        <AccordionButton>
                                            <AccordionIcon
                                                mr={2}
                                                flexShrink={0}
                                            />
                                            <Grid
                                                templateColumns="repeat(12, 1fr)"
                                                flex="1">
                                                <GridItem colSpan={4}>
                                                    <Text textAlign="left">
                                                        {permissionName}
                                                    </Text>
                                                </GridItem>
                                            </Grid>
                                        </AccordionButton>
                                        <AccordionPanel>
                                            {visibleScopes.map(
                                                (scopeNumber) => (
                                                    <Box
                                                        key={`${permissionName}-${scopeNumber}`}
                                                        className="table-data">
                                                        <Grid templateColumns="repeat(12, 1fr)">
                                                            <GridItem
                                                                colSpan={4}>
                                                                {
                                                                    config.scope
                                                                        .json[
                                                                        scopeNumber
                                                                    ]
                                                                }
                                                            </GridItem>
                                                            {renderCrudCheckboxes(
                                                                {
                                                                    permissionName,
                                                                    value,
                                                                    scopeNumber,
                                                                    disabled,
                                                                    submitData,
                                                                    onChange:
                                                                        handleOnChange,
                                                                },
                                                            )}
                                                        </Grid>
                                                    </Box>
                                                ),
                                            )}
                                        </AccordionPanel>
                                    </AccordionItem>
                                ))}
                        </Accordion>
                    )}
                </Box>
            </div>
        </div>
    );
};

export default PermissionsList;
